import { SelectGroupRole } from "@components/role/select-group-role";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { MemberIdentityRoleProps } from "@custom-types/member-types";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useToast } from "@hooks/use-toast";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import { GroupMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends BaseCompanyIdProps, MemberIdentityRoleProps {
  /** The selected group */
  selectedGroup: FaroCoreApiTypes.GroupDetailResponse;
}

/** Render and adjust member roles in group context */
export function GroupMemberRole({
  companyId,
  memberIdentityRole,
  selectedGroup,
}: Props): JSX.Element {
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const { canEditMember } = useHasUserValidRoleGroupLevel({ selectedGroup });

  const isCurrentUser = currentUser?.identity === memberIdentityRole.identity;
  const isCurrentUserGM = currentUser?.role === "COMPANY_MANAGER";

  /**
   * Return non-editable member role if:
   * - Member is current logged user and the role is group manager,
   *   as he might loose access to the group
   * - User has no permission to edit member
   */
  if ((isCurrentUser && isCurrentUserGM) || !canEditMember) {
    return (
      <span style={{ fontSize: DEFAULT_TEXT_FONT_SIZE }}>
        {formatUserRoleType(memberIdentityRole.role)}
      </span>
    );
  }

  /** Trigger updating member role and show success message */
  async function onRoleChange(role: FaroCoreApiTypes.GroupRole): Promise<void> {
    // Avoid same role click
    if (role === memberIdentityRole.role) {
      return;
    }

    trackEvent({
      name: GroupMemberEvents.changeRole,
      props: { role: role },
    });

    // Using unwrap makes sure that showToast is only shown if there is no error
    await dispatch(
      updateMemberRoleInGroup({
        companyId,
        groupId: selectedGroup.id,
        role,
        userIdentity: memberIdentityRole.identity,
      })
    ).unwrap();

    showToast({
      message: "Role Changed",
      description: (
        <RoleChangeSuccessDescription role={formatUserRoleType(role)} />
      ),
      type: "success",
    });
    // error is handled by the removeCompanyMember thunk in errors-slice
  }

  return (
    <SelectGroupRole
      selectedRole={memberIdentityRole.role as FaroCoreApiTypes.GroupRole}
      onChange={onRoleChange}
      isTableCell={true}
    />
  );
}
